import type { CitationGraph, CitationNode } from '../contracts/types.ts';

/**
 * 引用グラフの「読み方」。描画の手前までを担う。
 *
 * ここに置くのは **どのノードが中心からどれだけ離れていて、左右どちら側か** の判定、
 * 表示上限での絞り込み、列配置・縦配置の座標、辺の折れ線の経路、配置の検算。色は持たない。
 *
 * apps/web から切り出してあるのは、次を外部依存なしに検証できるようにするため。
 * - 深さ2を足しても三列（左＝この論文が引用 / 中央 / 右＝この論文を引用）が崩れないこと
 * - 同じグラフからは常に同じ並びが出ること（AGENTS.md「乱数を使わない」）
 * - 参考文献 200・被引用 300 のような大きなグラフでもカードが重ならないこと
 * - 狭い画面向けの縦配置が、与えた横幅に収まること
 */

export type GraphSide = 'left' | 'right';

export interface CitationRing {
  /** 中心からの距離。中心自身は含まれない。 */
  depth: number;
  side: GraphSide;
}

/**
 * 中心から辿って、各ノードの深さと向きを決める。
 *
 * **辺の向きだけから求める。** 深さ2を入れる前に保存したグラフにはノード側に深さの記録が
 * 無く、記録を足しても古い行には入らない。辺は当時から向きを持っているので、そちらを見る。
 * 幅優先で辿るので、複数の経路がある場合は浅い方を採る
 * （直接の参考文献でもあり、参考文献の参考文献でもある論文は「直接の参考文献」になる）。
 */
export function ringsFromEdges(graph: CitationGraph): Map<string, CitationRing> {
  const neighbors = new Map<string, Array<{ key: string; side: GraphSide }>>();
  const link = (from: string, to: string, side: GraphSide): void => {
    const list = neighbors.get(from);
    if (list) list.push({ key: to, side });
    else neighbors.set(from, [{ key: to, side }]);
  };
  for (const edge of graph.edges) {
    // references は「この論文が引用している」向き＝左、cited_by は右。
    const side: GraphSide = edge.direction === 'references' ? 'left' : 'right';
    link(edge.fromKey, edge.toKey, side);
    link(edge.toKey, edge.fromKey, side);
  }

  const rings = new Map<string, CitationRing>();
  const visited = new Set<string>([graph.centerKey]);
  let frontier = [graph.centerKey];
  for (let depth = 1; frontier.length > 0; depth += 1) {
    const next: string[] = [];
    for (const key of frontier) {
      for (const neighbor of neighbors.get(key) ?? []) {
        if (visited.has(neighbor.key)) continue;
        visited.add(neighbor.key);
        rings.set(neighbor.key, { depth, side: neighbor.side });
        next.push(neighbor.key);
      }
    }
    frontier = next;
  }
  return rings;
}

export interface VisibleNodes {
  /** 深さ1・左（この論文が引用している）。 */
  references: CitationNode[];
  /** 深さ1・右（この論文を引用している）。 */
  citedBy: CitationNode[];
  /** 深さ2・左（参考文献の参考文献）。 */
  depth2References: CitationNode[];
  /** 深さ2・右（被引用の被引用）。 */
  depth2CitedBy: CitationNode[];
  /** 深さ1の総数（表示上限で切る前）。見出しの件数に使う。 */
  referenceCount: number;
  citedByCount: number;
  depth2ReferenceCount: number;
  depth2CitedByCount: number;
  /** 表示上限で省いた件数の合計。0 でなければ利用者に伝える。 */
  dropped: number;
}

/**
 * 表示するノードを選ぶ。
 *
 * 並びは「PaperStock に解説がある → 引用数が多い → キーの辞書順」。
 * **最後をキーで決めるのは、引用数が同じノードの順序を再描画ごとにぶれさせないため。**
 */
export function selectVisibleNodes(
  graph: CitationGraph,
  options: { maxDepth: 1 | 2; maxPerSide: number; maxDepth2PerSide: number },
): VisibleNodes {
  const byKey = new Map(graph.nodes.map((n) => [n.key, n]));
  const rings = ringsFromEdges(graph);

  const byCitations = (a: CitationNode, b: CitationNode): number =>
    Number(b.internal) - Number(a.internal) ||
    (b.citationCount ?? 0) - (a.citationCount ?? 0) ||
    a.key.localeCompare(b.key);

  const collect = (side: GraphSide, depth: number): CitationNode[] =>
    Array.from(rings.entries())
      .filter(([, ring]) => ring.side === side && ring.depth === depth)
      .map(([key]) => byKey.get(key))
      .filter((n): n is CitationNode => Boolean(n))
      .sort(byCitations);

  const references = collect('left', 1);
  const citedBy = collect('right', 1);
  const depth2References = options.maxDepth >= 2 ? collect('left', 2) : [];
  const depth2CitedBy = options.maxDepth >= 2 ? collect('right', 2) : [];

  const shownReferences = references.slice(0, options.maxPerSide);
  const shownCitedBy = citedBy.slice(0, options.maxPerSide);
  const shownDepth2References = depth2References.slice(0, options.maxDepth2PerSide);
  const shownDepth2CitedBy = depth2CitedBy.slice(0, options.maxDepth2PerSide);

  return {
    references: shownReferences,
    citedBy: shownCitedBy,
    depth2References: shownDepth2References,
    depth2CitedBy: shownDepth2CitedBy,
    referenceCount: references.length,
    citedByCount: citedBy.length,
    depth2ReferenceCount: depth2References.length,
    depth2CitedByCount: depth2CitedBy.length,
    dropped:
      references.length -
      shownReferences.length +
      (citedBy.length - shownCitedBy.length) +
      (depth2References.length - shownDepth2References.length) +
      (depth2CitedBy.length - shownDepth2CitedBy.length),
  };
}

// ---------------------------------------------------------------------------
// 列配置
// ---------------------------------------------------------------------------

/**
 * 列配置の寸法。値は表示する側（apps/web）が決める。
 *
 * **配置の計算をここに置いてあるのは、乱数も画面幅も使わないことを検証できるようにするため。**
 * AGENTS.md はグラフレイアウトを決定論の対象に挙げている。
 */
export interface GraphGeometry {
  rowHeight: number;
  /** 1列に積む最大の行数。超えたら列を増やす。 */
  maxRows: number;
  columnWidth: number;
  /** 中心のノードとラベルのために空ける横幅。 */
  centerBand: number;
  /** 端から最初の列までの余白。 */
  edgeMargin: number;
  minRows: number;
  minHeight: number;
}

export type GraphOrientation = 'horizontal' | 'vertical';

export interface PlacedNode {
  node: CitationNode;
  x: number;
  y: number;
  side: GraphSide | 'center';
  /** 中心からの距離。0 が中心。 */
  depth: number;
  /**
   * このノードが属する帯（同じ側・同じ深さ）のうち、中心にいちばん近い車線と、いちばん遠い車線の座標。
   * 横配置では列の x、縦配置では行の y。中心ノードは自身の座標。
   *
   * 辺の折れ線を「帯と帯の隙間」に通すために持つ。ノード自身の座標だけでは、
   * 隣の帯との隙間がどこにあるか（同じ帯に列や行が何本あるか）が分からない。
   */
  laneInner: number;
  laneOuter: number;
}

export interface GraphLayout {
  /** 横配置（左＝参考文献 / 中央 / 右＝被引用）か、縦配置（上＝参考文献 / 中央 / 下＝被引用）か。 */
  orientation: GraphOrientation;
  width: number;
  height: number;
  /** 隣り合う列（横配置）または列（縦配置の1行内）の中心どうしの間隔。描画側がノード幅を決めるのに使う。 */
  columnWidth: number;
  placed: PlacedNode[];
}

interface Band {
  depth: number;
  nodes: CitationNode[];
}

function columnsFor(count: number, maxRows: number): number {
  return count === 0 ? 0 : Math.max(1, Math.ceil(count / maxRows));
}

/** 片側の列配置。深さの浅い帯を中心寄りに、深い帯を外側に積む。 */
function placeSide(
  bands: Band[],
  side: GraphSide,
  width: number,
  height: number,
  totalColumns: number,
  geometry: GraphGeometry,
): PlacedNode[] {
  const placed: PlacedNode[] = [];
  // 中心に近い列ほど内側。左は右向きに、右は左向きに積む。
  const xOfColumn = (columnFromCenter: number): number => {
    const offsetFromEdge = geometry.edgeMargin + (totalColumns - 1 - columnFromCenter) * geometry.columnWidth;
    return side === 'left' ? offsetFromEdge : width - offsetFromEdge;
  };
  let columnFromCenter = 0;
  for (const band of bands) {
    const columns = columnsFor(band.nodes.length, geometry.maxRows);
    if (columns === 0) continue;
    const perColumn = Math.ceil(band.nodes.length / columns);
    const laneInner = xOfColumn(columnFromCenter);
    const laneOuter = xOfColumn(columnFromCenter + columns - 1);
    band.nodes.forEach((node, index) => {
      const column = Math.floor(index / perColumn);
      const row = index % perColumn;
      const rowsInColumn = Math.min(perColumn, band.nodes.length - column * perColumn);
      const columnHeight = (rowsInColumn - 1) * geometry.rowHeight;
      const y = height / 2 - columnHeight / 2 + row * geometry.rowHeight;
      const x = xOfColumn(columnFromCenter + column);
      placed.push({ node, x, y, side, depth: band.depth, laneInner, laneOuter });
    });
    columnFromCenter += columns;
  }
  return placed;
}

/**
 * 三列（左＝この論文が引用 / 中央 / 右＝この論文を引用）に並べる。
 *
 * 深さ2は**同じ側のさらに外側の帯**へ置く。向きを混ぜないので、深さ2を足しても
 * 「左は過去、右は未来」という読み方は変わらない。
 */
export function layoutGraph(args: {
  centerKey: string;
  center: CitationNode | undefined;
  visible: VisibleNodes;
  geometry: GraphGeometry;
}): GraphLayout {
  const { geometry, visible } = args;
  const leftBands: Band[] = [
    { depth: 1, nodes: visible.references },
    { depth: 2, nodes: visible.depth2References },
  ];
  const rightBands: Band[] = [
    { depth: 1, nodes: visible.citedBy },
    { depth: 2, nodes: visible.depth2CitedBy },
  ];

  const columnsOf = (bands: Band[]): number =>
    Math.max(1, bands.reduce((sum, band) => sum + columnsFor(band.nodes.length, geometry.maxRows), 0));
  const columnsLeft = columnsOf(leftBands);
  const columnsRight = columnsOf(rightBands);
  const width = geometry.centerBand + (columnsLeft + columnsRight) * geometry.columnWidth;
  // 左右の列数が異なっても、最内列と中心の間隔を同じにする。
  // キャンバス中央へ固定すると、列が多い側だけ中心カードへ食い込む。
  const centerX = columnsLeft * geometry.columnWidth + geometry.centerBand / 2;
  const rows = Math.max(
    geometry.minRows,
    ...[...leftBands, ...rightBands].map((band) =>
      band.nodes.length === 0 ? 0 : Math.ceil(band.nodes.length / columnsFor(band.nodes.length, geometry.maxRows)),
    ),
  );
  const height = Math.max(geometry.minHeight, rows * geometry.rowHeight + 90);

  const left = placeSide(leftBands, 'left', width, height, columnsLeft, geometry);
  const right = placeSide(rightBands, 'right', width, height, columnsRight, geometry);
  const center: PlacedNode = {
    node: centerNode(args.centerKey, args.center),
    x: centerX,
    y: height / 2,
    side: 'center',
    depth: 0,
    laneInner: centerX,
    laneOuter: centerX,
  };

  return {
    orientation: 'horizontal',
    width,
    height,
    columnWidth: geometry.columnWidth,
    placed: [...left, center, ...right],
  };
}

function centerNode(centerKey: string, center: CitationNode | undefined): CitationNode {
  return (
    center ?? {
      key: centerKey,
      title: 'この論文',
      authors: [],
      internal: true,
      identifiers: {},
    }
  );
}

// ---------------------------------------------------------------------------
// 縦配置（狭い画面）
// ---------------------------------------------------------------------------

/**
 * 縦配置の寸法。
 *
 * 横配置は列幅 240px が固定で、最小でも 780px の横幅を要る。390px の端末では横スクロールが必須になり、
 * 「左が過去・右が未来」を一望できない。そこで狭い画面では列→行に転置し、
 * **参考文献の帯を上、中心、被引用の帯を下**に積む。深さ2は外側（いちばん上・いちばん下）の帯。
 *
 * 横幅は与えられた幅に合わせて列数と列幅を決める。列幅の下限（ノード幅の下限 150px ＋余白）を
 * 割る幅のときだけ、はみ出してスクロールになる。
 */
export interface VerticalGraphGeometry {
  /** 使える横幅（px）。この幅に収まる列数を選び、列幅を縮める。 */
  width: number;
  /** 列幅の下限。これを割るなら列数を増やさず、はみ出す。 */
  minColumnWidth: number;
  /** 列幅の上限。広めの画面で間延びさせない。 */
  maxColumnWidth: number;
  rowHeight: number;
  /** 帯（深さ）と帯の間に空ける高さ。折れ線の水平区間をここに通す。 */
  bandGap: number;
  /** 中心ノードのために空ける高さ。 */
  centerBand: number;
  /** 上下端の余白。 */
  edgeMargin: number;
  minHeight: number;
}

interface VerticalBand extends Band {
  side: GraphSide;
}

/**
 * 縦配置。列→行の転置。
 *
 * 各帯の中では、並び順の先頭（PaperStock に解説がある → 引用数が多い）を**中心にいちばん近い行**から
 * 埋める。端数の行は外側に来る。端数の行も列の格子に載せる（自由に中央寄せしない）。
 * 格子から外れた位置に置くと、辺の折れ線が通るはずの「列の隙間」が隣の列の真上に来て、カードを貫く。
 */
export function layoutGraphVertical(args: {
  centerKey: string;
  center: CitationNode | undefined;
  visible: VisibleNodes;
  geometry: VerticalGraphGeometry;
}): GraphLayout {
  const { geometry, visible } = args;
  const columns = Math.max(1, Math.floor(geometry.width / geometry.minColumnWidth));
  const columnWidth = Math.floor(
    Math.min(geometry.maxColumnWidth, Math.max(geometry.minColumnWidth, geometry.width / columns)),
  );
  // 列幅の下限を割る幅のときだけ、与えられた幅を超える（横スクロールになる）。
  const width = Math.max(geometry.width, columns * columnWidth);
  const centerX = width / 2;

  const bands: VerticalBand[] = [
    { side: 'left', depth: 2, nodes: visible.depth2References },
    { side: 'left', depth: 1, nodes: visible.references },
  ];
  const lowerBands: VerticalBand[] = [
    { side: 'right', depth: 1, nodes: visible.citedBy },
    { side: 'right', depth: 2, nodes: visible.depth2CitedBy },
  ];

  const placed: PlacedNode[] = [];
  const placeBand = (band: VerticalBand, top: number): number => {
    const rows = Math.ceil(band.nodes.length / columns);
    const yOfRow = (row: number): number => top + geometry.rowHeight / 2 + row * geometry.rowHeight;
    // 上の帯（参考文献）は下の行が中心寄り、下の帯（被引用）は上の行が中心寄り。
    const rowFromInner = (row: number): number => (band.side === 'left' ? rows - 1 - row : row);
    const laneInner = yOfRow(rowFromInner(0));
    const laneOuter = yOfRow(rowFromInner(rows - 1));
    band.nodes.forEach((node, index) => {
      const inner = Math.floor(index / columns);
      const column = index % columns;
      const nodesInRow = Math.min(columns, band.nodes.length - inner * columns);
      // 端数の行は、中心寄りの列から使う（列数 3 で 1 件なら真ん中の列）。
      const gridColumn = Math.floor((columns - nodesInRow) / 2) + column;
      const x = centerX + (gridColumn - (columns - 1) / 2) * columnWidth;
      placed.push({
        node,
        x,
        y: yOfRow(rowFromInner(inner)),
        side: band.side,
        depth: band.depth,
        laneInner,
        laneOuter,
      });
    });
    return rows * geometry.rowHeight;
  };

  let cursor = geometry.edgeMargin;
  for (const band of bands) {
    if (band.nodes.length === 0) continue;
    cursor += placeBand(band, cursor) + geometry.bandGap;
  }
  const centerY = cursor + geometry.centerBand / 2;
  cursor += geometry.centerBand;
  for (const band of lowerBands) {
    if (band.nodes.length === 0) continue;
    cursor += geometry.bandGap;
    cursor += placeBand(band, cursor);
  }
  const height = Math.max(geometry.minHeight, cursor + geometry.edgeMargin);

  const center: PlacedNode = {
    node: centerNode(args.centerKey, args.center),
    x: centerX,
    y: centerY,
    side: 'center',
    depth: 0,
    laneInner: centerY,
    laneOuter: centerY,
  };
  // 並び順は横配置と同じ（左＝参考文献 → 中心 → 右＝被引用）に揃える。
  const upper = placed.filter((item) => item.side === 'left');
  const lower = placed.filter((item) => item.side === 'right');
  return { orientation: 'vertical', width, height, columnWidth, placed: [...upper, center, ...lower] };
}

// ---------------------------------------------------------------------------
// 辺の経路と、配置の検算
// ---------------------------------------------------------------------------

export interface NodeSize {
  width: number;
  height: number;
}

/** ノードの見た目の大きさは描画側が決める（中心・深さで違う）。core は関数として受け取る。 */
export type NodeSizer = (node: PlacedNode) => NodeSize;

export interface Point {
  x: number;
  y: number;
}

/** 矩形ノードの境界上で、`target` へ向かう直線との交点。`outside` は矢印の分だけ外へ出す量。 */
export function boundaryPoint(from: PlacedNode, target: Point, size: NodeSize, outside = 0): Point {
  const dx = target.x - from.x;
  const dy = target.y - from.y;
  const scale =
    1 /
    Math.max(Math.abs(dx) / (size.width / 2 + outside), Math.abs(dy) / (size.height / 2 + outside), 1);
  return { x: from.x + dx * scale, y: from.y + dy * scale };
}

/** 重複点と、縦横に並んだ3点の真ん中（折れていない角）を落とす。同じ行の辺は直線になる。 */
function dedupePoints(points: Point[]): Point[] {
  const result: Point[] = [];
  for (const point of points) {
    const last = result[result.length - 1];
    if (last && Math.abs(last.x - point.x) < 1e-6 && Math.abs(last.y - point.y) < 1e-6) continue;
    const beforeLast = result[result.length - 2];
    if (
      last &&
      beforeLast &&
      ((Math.abs(beforeLast.y - last.y) < 1e-6 && Math.abs(last.y - point.y) < 1e-6) ||
        (Math.abs(beforeLast.x - last.x) < 1e-6 && Math.abs(last.x - point.x) < 1e-6))
    ) {
      result.pop();
    }
    result.push(point);
  }
  return result;
}

/** 直線。折れ線の通り道が定義できない辺（左右をまたぐ・同じ深さどうし）に使う。 */
export function routeEdgeStraight(
  _layout: GraphLayout,
  from: PlacedNode,
  to: PlacedNode,
  sizeOf: NodeSizer,
  arrowGap = 0,
): Point[] {
  return [boundaryPoint(from, to, sizeOf(from)), boundaryPoint(to, from, sizeOf(to), arrowGap)];
}

/**
 * 辺の折れ線。
 *
 * 直線で結ぶと、外側の列（深さ2や、1列に収まらない参考文献）から中心へ向かう辺が
 * 内側の列のカードを貫通する。そこで、**帯と帯の隙間**にある中線へいったん出てから相手へ向かう。
 *
 * - 横配置: 「外側ノードの辺 → 列間の垂直中線 → 相手の行 → 相手の辺」の3区間。
 * - 縦配置: 外側ノードは左右の辺から**列の隙間**へ出て、隙間を縦に通って帯の隙間へ入り、
 *   相手の列まで横に進んで相手の上下の辺に着く。同じ列の内側の行を貫かないための1区間が増える。
 *
 * 帯の隙間の位置は PlacedNode の laneInner / laneOuter から求める。ノードの大きさ（描画側の都合）は
 * 隙間の判定に使わず、境界点の計算にだけ使う。
 */
export function routeEdge(
  layout: GraphLayout,
  from: PlacedNode,
  to: PlacedNode,
  sizeOf: NodeSizer,
  arrowGap = 0,
): Point[] {
  const shareSide = from.side === to.side || from.side === 'center' || to.side === 'center';
  if (!shareSide || from.depth === to.depth) return routeEdgeStraight(layout, from, to, sizeOf, arrowGap);

  const outer = from.depth > to.depth ? from : to;
  const inner = outer === from ? to : from;
  const outsideOf = (node: PlacedNode): number => (node === to ? arrowGap : 0);
  const mid = (outer.laneInner + inner.laneOuter) / 2;

  if (layout.orientation === 'horizontal') {
    const points = [
      boundaryPoint(from, { x: mid, y: from.y }, sizeOf(from), outsideOf(from)),
      { x: mid, y: from.y },
      { x: mid, y: to.y },
      boundaryPoint(to, { x: mid, y: to.y }, sizeOf(to), outsideOf(to)),
    ];
    return dedupePoints(points);
  }

  // 縦配置。外側ノードから見て中心寄りの列の隙間へ出る。同じ列なら右側の隙間を使う。
  const gapDirection = outer.x < inner.x ? 1 : outer.x > inner.x ? -1 : 1;
  const gapX = outer.x + (gapDirection * layout.columnWidth) / 2;
  const points = [
    boundaryPoint(outer, { x: gapX, y: outer.y }, sizeOf(outer), outsideOf(outer)),
    { x: gapX, y: outer.y },
    { x: gapX, y: mid },
    { x: inner.x, y: mid },
    boundaryPoint(inner, { x: inner.x, y: mid }, sizeOf(inner), outsideOf(inner)),
  ];
  if (from === inner) points.reverse();
  return dedupePoints(points);
}

export type EdgeRouter = typeof routeEdge;

interface Rect {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

function rectOf(node: PlacedNode, size: NodeSize, inset = 0): Rect {
  return {
    left: node.x - size.width / 2 + inset,
    right: node.x + size.width / 2 - inset,
    top: node.y - size.height / 2 + inset,
    bottom: node.y + size.height / 2 - inset,
  };
}

/** 線分が矩形の内部を通るか（Liang–Barsky）。境界に触れるだけの線分は数えない。 */
function segmentCrossesRect(a: Point, b: Point, rect: Rect): boolean {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  let t0 = 0;
  let t1 = 1;
  const clips: Array<[number, number]> = [
    [-dx, a.x - rect.left],
    [dx, rect.right - a.x],
    [-dy, a.y - rect.top],
    [dy, rect.bottom - a.y],
  ];
  for (const [p, q] of clips) {
    if (p === 0) {
      if (q < 0) return false;
      continue;
    }
    const r = q / p;
    if (p < 0) {
      if (r > t1) return false;
      if (r > t0) t0 = r;
    } else {
      if (r < t0) return false;
      if (r < t1) t1 = r;
    }
  }
  return t0 < t1;
}

/**
 * 配置済みノードの矩形どうしが交差している組の数。0 でなければ配置が壊れている。
 * 辺だけが接している組は数えない。
 */
export function countNodeOverlaps(layout: GraphLayout, sizeOf: NodeSizer): number {
  const rects = layout.placed.map((node) => rectOf(node, sizeOf(node)));
  let overlaps = 0;
  for (let i = 0; i < rects.length; i += 1) {
    const a = rects[i]!;
    for (let j = i + 1; j < rects.length; j += 1) {
      const b = rects[j]!;
      if (a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom) overlaps += 1;
    }
  }
  return overlaps;
}

/**
 * 端点でないノードの矩形を通る辺の本数。1本の辺が複数のノードを貫いても 1 と数える。
 * 経路は既定で `routeEdge`。直線との比較のために差し替えられる。
 */
export function countEdgeThroughNodes(
  layout: GraphLayout,
  edges: Array<{ fromKey: string; toKey: string }>,
  sizeOf: NodeSizer,
  route: EdgeRouter = routeEdge,
): number {
  const byKey = new Map(layout.placed.map((item) => [item.node.key, item]));
  // 境界をかすめるだけの線分を数えないよう、矩形をわずかに内側へ縮めて判定する。
  const rects = layout.placed.map((node) => ({ key: node.node.key, rect: rectOf(node, sizeOf(node), 0.5) }));
  let count = 0;
  for (const edge of edges) {
    const from = byKey.get(edge.fromKey);
    const to = byKey.get(edge.toKey);
    if (!from || !to || from === to) continue;
    const points = route(layout, from, to, sizeOf);
    const pierced = rects.some(({ key, rect }) => {
      if (key === edge.fromKey || key === edge.toKey) return false;
      for (let i = 1; i < points.length; i += 1) {
        if (segmentCrossesRect(points[i - 1]!, points[i]!, rect)) return true;
      }
      return false;
    });
    if (pierced) count += 1;
  }
  return count;
}
